import { cloneDeep } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { calcCurrentDuration, calcCurrentPrice, isValidVoPackage } from '../../../utils/packagesUtils';
import { getVoCalculator, isWordCountCalculator, VO_TYPE } from '../../../utils/calculator';
import calculateVoData from '../../../utils/voiceOver';
import {
    CALC_MAX,
    RECOMMENDATION_ELIGIBLE_PACKAGES
} from '../../../utils/packagesUtils/constants';
import { RECOMMENDED_TYPES } from '../../../utils/packagesUtils/types/recommendedTypes';

export const getRecommendedType = ({ id, queryParameters = {}, features = [] }) => {
    const { pckg_id: packageId, extra_fast: extraFast } = queryParameters;

    const isReferencedPackage = packageId === id.toString();
    const hasCustomizedFeature = !!features.find(({ customized }) => customized);
    const isCustomizedEligible = hasCustomizedFeature || !!extraFast;
    const isRecommendedEligible = RECOMMENDATION_ELIGIBLE_PACKAGES.includes(id);

    if (isReferencedPackage && isCustomizedEligible) {
        return RECOMMENDED_TYPES.CUSTOMIZED;
    } else if (isReferencedPackage && isRecommendedEligible) {
        return RECOMMENDED_TYPES.RECOMMENDED;
    }

    return '';
};

export const sortPackages = (packageList) => {
    packageList.sort((package1, package2) => {
        if (package1.id < package2.id) {
            return -1;
        } else {
            return 1;
        }
    });
};

export const isFeatureFiltered = (feature, filteredFactors) => {
    const filteredFeature = filteredFactors.find((filteredFactor) => filteredFactor === feature.name);

    return !!filteredFeature;
};

const mapPackagesFeatures = (packageList, queryParameters) => {
    const mappedFeatures = {};
    const includedFeatureIds = new Set();

    const filteredFactors = getFilteredFactors(queryParameters);
    const filteredPackageId = queryParameters.pckg_id ? parseInt(queryParameters.pckg_id, 10) : -1;

    packageList.forEach((packageData) => {
        packageData.features.forEach((feature) => {
            if (!mappedFeatures[feature.id]) {
                mappedFeatures[feature.id] = {
                    featureData: feature,
                    includedCount: 0
                };
            }

            if (feature.included) {
                mappedFeatures[feature.id].includedCount++;
                includedFeatureIds.add(feature.id);
            } else if (filteredPackageId === packageData.id && isFeatureFiltered(feature, filteredFactors)) {
                feature.included = true;
                feature.customized = true;
                mappedFeatures[feature.id].includedCount++;
                includedFeatureIds.add(feature.id);
            }
        });
    });

    return {
        mappedFeatures,
        includedFeatureIds
    };
};

export const alignPackagesFeatures = (packageList, queryParameters) => {
    if (packageList.length !== 3) {
        return;
    }

    const { mappedFeatures, includedFeatureIds } = mapPackagesFeatures(packageList, queryParameters);

    packageList.forEach((packageData) => {
        // filter features which are not included in all 3 packages
        packageData.features = packageData.features.filter((feature) => mappedFeatures[feature.id].includedCount > 0);

        // fill/filter features lists which are smaller/bigger than the featureIds list
        const { features } = packageData;
        if (features.length < includedFeatureIds.size) {
            includedFeatureIds.forEach((featureId) => {
                if (features.find((feature) => feature.id === featureId) === undefined) {
                    features.push({ ...mappedFeatures[featureId].featureData, included: false, customized: false });
                }
            });
        } else if (features.length > includedFeatureIds.size) {
            packageData.features = features.filter((feature) => includedFeatureIds.has(feature.id));
        }
    });
};

export const sortPackagesFeatures = (packageList) => {
    const featuresOrder = {};
    let orderIndex = 1;

    packageList.slice().reverse().forEach((packageData) => {
        packageData.features.forEach((feature) => {
            if (feature.included && !featuresOrder[feature.id]) {
                featuresOrder[feature.id] = orderIndex++;
            }
        });
    });

    packageList.forEach((packageData) => {
        packageData.features.sort((feature1, feature2) => {
            const featureOrder1 = featuresOrder[feature1.id],
                featureOrder2 = featuresOrder[feature2.id];
            if (featureOrder1 < featureOrder2) {
                return -1;
            } else {
                return 1;
            }
        });
    });
};

export const calculateVoPackageData = (packageData, voData) => {
    let newCurrentPrice;
    const { calculators, extraFast } = packageData;
    const newCalculators = calculators.map((calculator) => {
        if (calculator.id === voData.calculatorId) {
            newCurrentPrice = voData.price;
            return { ...calculator, type: VO_TYPE, currentPrice: voData.price };
        } else {
            return calculator;
        }
    });

    const newFeatures = packageData.features.map((feature) => {
        const voDataFeature = voData.purchaseExtras[feature.id];

        return { ...feature, ...voDataFeature };
    });

    const newPackageData = {
        ...packageData,
        features: newFeatures,
        calculators: newCalculators,
        currentDuration: voData.duration
    };

    if (extraFast) {
        const voDataExtraFast = voData.purchaseExtras[extraFast.id];
        newPackageData.extraFast = { ...extraFast, ...voDataExtraFast };
    }

    const currentPrice = getNewPrice(packageData, newCurrentPrice);

    return {
        ...newPackageData,
        currentPrice
    };
};

const getFilteredFactors = (queryParameters) => {
    const filteredFactors = queryParameters.filtered_factors;

    return filteredFactors ? filteredFactors.split(',') : [];
};

const formatCustomExtras = (customExtras) => customExtras.map((customExtra) => {
    const formattedDuration = formatDuration(customExtra.duration);
    return {
        ...customExtra,
        duration: formattedDuration,
        currentDuration: formattedDuration
    };
});

const formatFeatures = (features, queryParameters) => {
    const filteredFactors = getFilteredFactors(queryParameters);

    return features.map((feature) => {
        const isFilteredFactor = !!filteredFactors.find((filteredFactor) => filteredFactor === feature.name);
        const amount = isFilteredFactor ? 1 : 0;
        const currentPrice = feature.price;
        const formattedDuration = formatDuration(feature.duration);

        return { ...feature, duration: formattedDuration, amount, currentPrice, currentDuration: formattedDuration };
    });
};

const formatCalculators = (calculators) => calculators.map((calculator) => ({
    ...calculator,
    currentValue: calculator.value
}));

const formatExtraFast = (extraFast, { extra_fast }) => {
    const duration = formatDuration(extraFast.duration);
    const currentValue = extra_fast ? 1 : 0;
    const currentPrice = extraFast.price;

    return { ...extraFast, currentValue, currentPrice, duration, currentDuration: duration };
};

const formatDuration = (duration) => duration > 0 ? duration / 24 : 0;

export const getNewPrice = (packageData, newCurrentPrice) => {
    // If new calculated price is lower than the initial package price we'll set it to be the initial price
    // The calculator itself will still be updated with the inserted value
    if (newCurrentPrice < packageData.price) {
        newCurrentPrice = packageData.price;
    }

    return newCurrentPrice;
};

const formatPackages = (packageList, queryParameters) => {
    sortPackages(packageList);
    alignPackagesFeatures(packageList, queryParameters);
    sortPackagesFeatures(packageList);
};

export const formatVoCalculators = (calculators, wordCount) =>
    calculators.map((calculator) => {
        const { value } = calculator;
        let currentValue = value;

        if (isWordCountCalculator(calculator)) {
            currentValue = Math.min(wordCount, CALC_MAX);
        }

        return { ...calculator, currentValue };
    });

export const formatVoPackageData = (packageData, scriptLength) => {
    packageData.calculators = formatVoCalculators(packageData.calculators, scriptLength);
    const { id: calculatorId } = getVoCalculator(packageData.calculators);
    const voData = calculateVoData({ scriptLength, calculatorId, packageData });

    return calculateVoPackageData(packageData, voData);
};

/**
 * Get all the features that are not included in any of the packages.
 * all features are formatted.
 * @param {object[]} packages packages initial prop from phoenix
 * @returns {object[]} all features that are not included in any of the packages.
 */
export const extractNotIncludedFeatures = (packages) => {
    if (!packages?.packageList) {
        return [];
    }

    const { queryParameters } = getContext();
    const { mappedFeatures } = mapPackagesFeatures(packages.packageList, queryParameters);
    const notIncludedFeatures = Object.values(mappedFeatures)
        .filter(({ includedCount }) => includedCount === 0)
        .map(({ featureData }) => featureData);

    return formatFeatures(notIncludedFeatures, queryParameters);
};

export default (packages, general) => {
    let packageList = [];
    const { queryParameters } = getContext();
    const { subCategoryId } = general;
    const { word_count: wordCount } = queryParameters;

    if (packages && packages.packageList) {
        const clonedPackages = cloneDeep(packages);
        formatPackages(clonedPackages.packageList, queryParameters);
        packageList = clonedPackages.packageList;
    }

    return packageList.map((packageData) => {
        const { duration, id, calculators, extraFast, features, customExtras } = packageData;

        const formattedPackageData = {
            ...packageData,
            duration: formatDuration(duration),
            calculators: calculators ? formatCalculators(calculators) : [],
            extraFast: extraFast ? formatExtraFast(extraFast, queryParameters) : null,
            recommendedType: getRecommendedType({ id, queryParameters, features }),
            features: features ? formatFeatures(features, queryParameters) : [],
            customExtras: customExtras ? formatCustomExtras(customExtras) : []
        };

        if (isValidVoPackage(calculators, subCategoryId) && Number(wordCount)) {
            return formatVoPackageData(formattedPackageData, wordCount);
        }

        return {
            ...formattedPackageData,
            currentPrice: calcCurrentPrice(formattedPackageData),
            currentDuration: calcCurrentDuration(formattedPackageData)
        };
    });
};
